import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Music,
  Edit3,
  User,
  FileText,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { IHymnProps } from "../../types";
import { PerformRequest, usePerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { NonPaginatedResponse } from "../../lib/responses";
import { HttpSuccessCodesObj } from "../../lib/data";
import MegaLoader from "../../components/MegaLoader";

const EditHymn: React.FC = () => {
  const navigate = useNavigate();
  const { hymnID } = useParams();
  const [isLoading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<IHymnProps>>({
    title: "",
    summary: "",
    verses: [{ id: "1", lyrics: "" }],
    author: {
      profile: "",
      name: "",
      link: "",
    },
    pub: {
      year: new Date().getFullYear().toString(),
      copyright: "Public Domain",
    },
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });

  const { data: hymnData, isLoading: isFetchingHymn } = usePerformRequest<
    NonPaginatedResponse<IHymnProps>
  >({
    method: Endpoints.GetHymn(hymnID as string).method,
    route: Endpoints.GetHymn(hymnID as string).route,
  });

  useEffect(() => {
    if (hymnData?.data) {
      setFormData({
        title: hymnData.data.title,
        summary: hymnData.data.summary,
        verses: hymnData.data.verses,
        author: {
          profile: hymnData.data.author?.profile || "",
          name: hymnData.data.author?.name || "",
          link: hymnData.data.author?.link || "",
        },
        pub: {
          year: hymnData.data.pub?.year || "",
          copyright: hymnData.data.pub?.copyright || "",
        },
        createdAt: hymnData.data.createdAt,
        updatedAt: hymnData.data.updatedAt,
      });
    }
  }, [hymnData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAuthorChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      author: {
        ...prev.author!,
        [field]: value,
      },
    }));
  };

  const handleCopyrightChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      pub: {
        ...prev.pub!,
        [field]: value,
      },
    }));
  };

  const handleVerseChange = (index: number, value: string) => {
    const newVerses = [...formData.verses!];
    newVerses[index].lyrics = value;
    setFormData((prev) => ({
      ...prev,
      verses: newVerses,
    }));
  };

  const addVerse = () => {
    const newVerse = {
      id: (formData.verses!.length + 1).toString(),
      lyrics: "",
    };
    setFormData((prev) => ({
      ...prev,
      verses: [...prev.verses!, newVerse],
    }));
  };

  const removeVerse = (index: number) => {
    if (formData.verses!.length > 1) {
      const newVerses = formData.verses!.filter((_, i) => i !== index);
      // Reorder IDs
      newVerses.forEach((verse, i) => {
        verse.id = (i + 1).toString();
      });
      setFormData((prev) => ({
        ...prev,
        verses: newVerses,
      }));
    }
  };

  const HandleUpdateHymn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hymnID) return;

    try {
      setLoading(true);
      const { title, summary, verses, author, pub } = formData;
      const r = await PerformRequest<NonPaginatedResponse<IHymnProps>>({
        method: Endpoints.UpdateHymn(hymnID).method,
        route: Endpoints.UpdateHymn(hymnID).route,
        data: {
          title,
          verses: verses?.map((v) => {
            return { id: v.id, lyrics: v.lyrics };
          }),
          author: author?.name,
          authorProfile: author?.profile,
          authorLink: author?.link,
          summary,
          pubYear: pub?.year,
          pubCopyright: pub?.copyright,
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);

      if (r) {
        if (r.status === HttpSuccessCodesObj.Success) {
          // Success - navigate to hymns page
          navigate(`/d/hymn/${r.data.data.id}`);
        } else {
          console.error("Failed to update hymn:", r);
          // You can add error handling here
        }
      }
    } catch (error) {
      console.error("Error updating hymn:", error);
      // You can add error handling here
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHymns = () => {
    navigate("/d/hymns");
  };

  if (isFetchingHymn) {
    return <MegaLoader />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden text-gray-100 font-sans">
      {/* Background Grain */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-between mb-10"
        >
          <motion.button
            whileHover={{ x: -3 }}
            onClick={handleBackToHymns}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1" />
            <span className="text-sm font-medium">Back to Hymns</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center space-x-3"
          >
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Music className="w-6 h-6 text-purple-400" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              Edit Hymn
            </h1>
          </motion.div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <form onSubmit={HandleUpdateHymn} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8 hover:border-gray-700 transition-colors duration-300">
              <div className="flex items-center space-x-3 mb-8 border-b border-gray-800 pb-4">
                <Edit3 className="w-5 h-5 text-purple-400" />
                <h2 className="text-lg font-semibold text-white">
                  Basic Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-gray-400 text-sm font-medium">
                    Hymn Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-200"
                    placeholder="e.g. Amazing Grace"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-gray-400 text-sm font-medium">
                    Copyright Year
                  </label>
                  <input
                    type="text"
                    value={formData.pub?.year}
                    onChange={(e) =>
                      handleCopyrightChange("year", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-200"
                    placeholder="e.g. 1779"
                  />
                </div>
              </div>

              <div className="mt-6 space-y-2">
                <label className="text-gray-400 text-sm font-medium">
                  Summary *
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => handleInputChange("summary", e.target.value)}
                  required
                  rows={3}
                  className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-200 resize-none"
                  placeholder="Brief description of the hymn..."
                />
              </div>
            </div>

            {/* Author Information */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8 hover:border-gray-700 transition-colors duration-300">
              <div className="flex items-center space-x-3 mb-8 border-b border-gray-800 pb-4">
                <User className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-white">
                  Author Details
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-gray-400 text-sm font-medium">
                    Author Name
                  </label>
                  <input
                    type="text"
                    value={formData.author?.name}
                    onChange={(e) => handleAuthorChange("name", e.target.value)}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                    placeholder="Author's name"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-gray-400 text-sm font-medium">
                    Author Link (Optional)
                  </label>
                  <input
                    type="url"
                    value={formData.author?.link}
                    onChange={(e) => handleAuthorChange("link", e.target.value)}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="mt-6 space-y-2">
                <label className="text-gray-400 text-sm font-medium">
                  Author Profile
                </label>
                <textarea
                  value={formData.author?.profile}
                  onChange={(e) =>
                    handleAuthorChange("profile", e.target.value)
                  }
                  rows={3}
                  className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none"
                  placeholder="Brief biography or description of the author..."
                />
              </div>
            </div>

            {/* Verses */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8 hover:border-gray-700 transition-colors duration-300">
              <div className="flex items-center justify-between mb-8 border-b border-gray-800 pb-4">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-green-400" />
                  <h2 className="text-lg font-semibold text-white">
                    Verses & Lyrics
                  </h2>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={addVerse}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-gray-800 text-gray-200 rounded-lg hover:bg-gray-700 border border-gray-700 transition-all duration-200 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Verse</span>
                </motion.button>
              </div>

              <div className="space-y-6">
                <AnimatePresence mode="wait">
                  {formData.verses!.map((verse, index) => (
                    <motion.div
                      key={verse.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                      className="relative group"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 font-mono text-sm">
                          {index + 1}
                        </div>

                        <div className="flex-1 space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-gray-400 text-sm font-medium">
                              Verse Lyrics *
                            </label>
                            {formData.verses!.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeVerse(index)}
                                className="text-gray-500 hover:text-red-400 transition-colors duration-200 text-xs flex items-center"
                              >
                                <Trash2 className="w-3 h-3 mr-1" /> Remove
                              </button>
                            )}
                          </div>
                          <textarea
                            value={verse.lyrics}
                            onChange={(e) =>
                              handleVerseChange(index, e.target.value)
                            }
                            required
                            rows={5}
                            className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 resize-y font-normal"
                            placeholder="Enter verse lyrics..."
                          />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isLoading}
                onClick={HandleUpdateHymn}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <AnimatePresence mode="wait">
                  {isLoading ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"
                    />
                  ) : (
                    <motion.div
                      key="text"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center space-x-2"
                    >
                      <Save className="w-5 h-5" />
                      <span>Update Hymn</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default EditHymn;
